import { useCallback, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Loader2, UserRoundCheck } from 'lucide-react';
import { invoicePayerPaymentDateBackfillAPI } from '../../lib/adminInvoicePayerBackfillApi';
import { getErrorMessage } from '../../lib/errors';
import { useJobPolling, useJobResume, JobProgressLine, JobErrorBlock } from './JobPollingPanel';

// Preview's per-row outcome (routes/admin_invoice_payer_payment_date_backfill.py::
// _row_outcome) -- what Apply would actually do for this row right now, so a
// candidate never silently "vanishes" into the sample with no indication it
// would be skipped.
function OutcomeBadge({ outcome, reason }) {
  if (outcome === 'stamped') {
    return <Badge variant="outline" className="text-[10px] border-green-400 text-green-700 dark:border-green-600 dark:text-green-400">would stamp</Badge>;
  }
  if (outcome === 'already-stamped') {
    return <Badge variant="outline" className="text-[10px]">already stamped</Badge>;
  }
  return (
    <Badge variant="outline" className="text-[10px] border-amber-400 text-amber-700 dark:border-amber-600 dark:text-amber-400" title={reason || undefined}>
      SKIPPED{reason ? ` (${reason})` : ''}
    </Badge>
  );
}

const ACCOUNT_CLASSIFICATION_LABELS = {
  no_account_doc: 'no bank account record',
  account_unassigned: 'account not yet assigned to an entity',
};

// The genuinely-unmappable residue behind "account entity unresolvable"
// rows, deduped by account (routes/admin_invoice_payer_payment_date_backfill.py::
// _preview's accounts_needing_entity_mapping) -- real operator data entry
// (assign the account an entity on the Bank Accounts page), not something
// a code fix can resolve, so this is surfaced as a named list to act on
// once instead of leaving the operator to spot the pattern across N rows.
function AccountsNeedingEntityMapping({ accounts }) {
  if (!accounts || accounts.length === 0) return null;
  return (
    <div className="border rounded-lg p-2 space-y-1" data-testid="invoice-payer-backfill-unmapped-accounts">
      <p className="text-xs font-medium">Accounts needing entity mapping</p>
      <p className="text-xs text-muted-foreground">
        These bank accounts are why some invoices above are SKIPPED -- assign each an entity on
        the Bank Accounts page, then re-run Preview.
      </p>
      <table className="w-full text-xs">
        <thead>
          <tr className="border-b bg-muted/50">
            <th className="text-left px-2 py-1 font-medium">Account</th>
            <th className="text-left px-2 py-1 font-medium">Bank</th>
            <th className="text-left px-2 py-1 font-medium">Issue</th>
            <th className="text-left px-2 py-1 font-medium">Affected invoices</th>
          </tr>
        </thead>
        <tbody>
          {accounts.map((a) => (
            <tr key={a.bank_account_id} className="border-b last:border-0" data-testid={`invoice-payer-backfill-unmapped-account-${a.bank_account_id}`}>
              <td className="px-2 py-1 font-mono">{a.bank_account_id}</td>
              <td className="px-2 py-1">{a.bank_name || '-'}</td>
              <td className="px-2 py-1 text-amber-600">{ACCOUNT_CLASSIFICATION_LABELS[a.classification] || a.classification}</td>
              <td className="px-2 py-1">{a.affected_invoice_count}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function InvoicePayerPaymentDateBackfillPanel() {
  const [preview, setPreview] = useState(null);
  const [previewStarting, setPreviewStarting] = useState(false);
  const [previewFailed, setPreviewFailed] = useState(null);

  const [applyStarting, setApplyStarting] = useState(false);
  const [applyResult, setApplyResult] = useState(null);
  const [applyFailed, setApplyFailed] = useState(null);
  const [applyProgress, setApplyProgress] = useState(null);

  const previewPoll = useJobPolling({
    fetchStatus: invoicePayerPaymentDateBackfillAPI.jobStatus,
    onCompleted: useCallback((result) => {
      setPreview(result);
      setPreviewFailed(null);
    }, []),
    onFailed: useCallback((error) => {
      setPreviewFailed(error);
    }, []),
  });

  const applyPoll = useJobPolling({
    fetchStatus: invoicePayerPaymentDateBackfillAPI.jobStatus,
    onCompleted: useCallback((result) => {
      setApplyResult(result);
      setApplyFailed(null);
      setApplyProgress(null);
    }, []),
    onFailed: useCallback((error) => {
      setApplyFailed(error);
      setApplyProgress(null);
    }, []),
    onProgress: useCallback((progress) => {
      setApplyProgress(progress);
    }, []),
  });

  // Pick up an already-running Preview/Apply job on page load/refresh.
  useJobResume({ fetchLatest: invoicePayerPaymentDateBackfillAPI.previewJobLatest, poll: previewPoll });
  useJobResume({ fetchLatest: invoicePayerPaymentDateBackfillAPI.jobLatest, poll: applyPoll });

  const runPreview = async () => {
    setPreviewStarting(true);
    setPreviewFailed(null);
    setApplyResult(null);
    setApplyFailed(null);
    try {
      const { data } = await invoicePayerPaymentDateBackfillAPI.previewStart();
      if (data.job_id) {
        previewPoll.start(data.job_id);
      }
    } catch (err) {
      setPreviewFailed(getErrorMessage(err) || 'Failed to start preview.');
    } finally {
      setPreviewStarting(false);
    }
  };

  const previewBusy = previewStarting || previewPoll.polling;

  const runApply = async () => {
    if (!window.confirm(
      `Stamp payer on ${preview?.invoice_count || 0} already-paid invoice(s) that are missing ` +
      'it? Payment Date (paid_at) is already present on each -- only payer_entity_id/' +
      'payer_entity_name are backfilled here.'
    )) return;
    setApplyStarting(true);
    setApplyFailed(null);
    setApplyResult(null);
    setApplyProgress(null);
    try {
      const { data } = await invoicePayerPaymentDateBackfillAPI.applyStart();
      if (data.job_id) {
        applyPoll.start(data.job_id);
      }
    } catch (err) {
      setApplyFailed(getErrorMessage(err) || 'Failed to start apply.');
    } finally {
      setApplyStarting(false);
    }
  };

  const applyBusy = applyStarting || applyPoll.polling;

  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-center gap-2">
          <UserRoundCheck className="h-4 w-4 text-muted-foreground" />
          <CardTitle className="text-base">Backfill Payer + Payment Date</CardTitle>
        </div>
        <p className="text-xs text-muted-foreground">
          Stamps payer_entity_id/payer_entity_name (the Invoices page Payer column) on invoices
          that already have a confirmed match and a payment stamp, but were reconciled before
          payer stamping existed. Payment Date (paid_at) is already present on these rows.
          Two-step: Preview, then Apply. Both run in the background and this panel polls for
          progress -- it is safe to navigate away and come back.
        </p>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex gap-2">
          <Button
            variant="outline" size="sm" onClick={runPreview} disabled={previewBusy}
            data-testid="invoice-payer-backfill-preview"
          >
            {previewBusy ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : null}
            Preview
          </Button>
          {preview && !preview.error && preview.invoice_count > 0 && (
            <Button
              variant="default"
              size="sm"
              onClick={runApply}
              disabled={applyBusy}
              data-testid="invoice-payer-backfill-apply"
            >
              {applyBusy ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : null}
              Apply to {preview.invoice_count} Invoices
            </Button>
          )}
        </div>

        <JobErrorBlock error={previewFailed} testId="invoice-payer-backfill-preview-error" />

        {preview && (
          <div className="space-y-2" data-testid="invoice-payer-backfill-preview-result">
            <div className="flex flex-wrap gap-3 text-xs">
              <span>Invoices to stamp: <strong>{preview.invoice_count}</strong></span>
              {preview.multi_confirmed_invoice_count > 0 && (
                <span className="text-amber-600">
                  Double-confirmed (pre-existing anomaly): <strong>{preview.multi_confirmed_invoice_count}</strong>
                </span>
              )}
            </div>
            <p className="text-xs text-muted-foreground">{preview.message}</p>
            {preview.invoices && preview.invoices.length > 0 && (
              <div className="overflow-x-auto border rounded-lg max-h-64 overflow-y-auto" data-testid="invoice-payer-backfill-preview-rows">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="border-b bg-muted/50 sticky top-0">
                      <th className="text-left px-2 py-1 font-medium">Invoice</th>
                      <th className="text-left px-2 py-1 font-medium">Supplier</th>
                      <th className="text-left px-2 py-1 font-medium">Amount</th>
                      <th className="text-left px-2 py-1 font-medium">Payment Date</th>
                      <th className="text-left px-2 py-1 font-medium">Payer</th>
                      <th className="text-left px-2 py-1 font-medium">Outcome</th>
                    </tr>
                  </thead>
                  <tbody>
                    {preview.invoices.map((inv) => (
                      <tr key={inv.id} className="border-b last:border-0" data-testid={`invoice-payer-backfill-row-${inv.id}`}>
                        <td className="px-2 py-1 font-mono">{inv.invoice_number || inv.id}</td>
                        <td className="px-2 py-1">{inv.supplier_client_name || '-'}</td>
                        <td className="px-2 py-1">{inv.amount} {inv.currency}</td>
                        <td className="px-2 py-1 whitespace-nowrap">{inv.paid_at || '-'}</td>
                        <td className="px-2 py-1 text-muted-foreground">{'—'}</td>
                        <td className="px-2 py-1"><OutcomeBadge outcome={inv.outcome} reason={inv.skip_reason} /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <AccountsNeedingEntityMapping accounts={preview.accounts_needing_entity_mapping} />
          </div>
        )}

        {applyPoll.polling && (
          <JobProgressLine progress={applyProgress} testId="invoice-payer-backfill-apply-progress">
            {(p) => (
              <>
                Stamped {p.stamped ?? 0} of {p.total ?? '?'}
                {p.scanned != null ? ` (scanned ${p.scanned})` : ''}
              </>
            )}
          </JobProgressLine>
        )}

        <JobErrorBlock error={applyFailed} testId="invoice-payer-backfill-apply-error" />

        {applyResult && !applyFailed && (
          <div
            className="p-3 rounded text-xs border bg-green-50 border-green-200 dark:bg-green-950/30 dark:border-green-900"
            data-testid="invoice-payer-backfill-apply-result"
          >
            <div className="space-y-1">
              <p><strong>Stamped:</strong> {applyResult.stamped} of {applyResult.invoice_count}</p>
              {applyResult.skipped && applyResult.skipped.length > 0 && (
                <p className="text-amber-600"><strong>Skipped:</strong> {applyResult.skipped.length} (see skipped[] for reasons)</p>
              )}
              {applyResult.multi_confirmed_invoice_count > 0 && (
                <Badge variant="outline" className="text-[10px]">
                  {applyResult.multi_confirmed_invoice_count} double-confirmed invoice(s) resolved to their most recent match
                </Badge>
              )}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
